import { Component, Input, OnInit } from "@angular/core";
import { VisualizationOptionsType } from "@arch/_shared/types/options-visualization";
import {
    CompanyEntity,
    PointEntity,
    SerieEntity,
    SerieLabelType,
    VisualizationEntity
} from "@arch/domain/entity";
import { HighchartsRenderComponent } from "../../highcharts-render/highcharts-render.component";
import { SharedMaterialModule } from "@app/shared/modules/shared-material.module";
import { SharedDsnModule } from "@app/shared/modules/shared-dsn.module";
import { GetIndicatorComparationsController } from "@arch/presentation/controllers";
import { IndicatorComparationsResultType } from "@arch/domain/application/usecase";
import { StringUtils } from "@arch/_shared/utils/String.utils";
import { ColorsService } from "@arch/domain/application/services/colors.service";

@Component({
    selector: "app-special-chart-column-range",
    imports: [HighchartsRenderComponent, SharedMaterialModule, SharedDsnModule],
    templateUrl: "./special-chart-column-range.component.html",
    styleUrl: "./special-chart-column-range.component.scss"
})
export class SpecialChartColumnRangeComponent implements OnInit {
    @Input({ required: true }) series: SerieEntity[];
    @Input({ required: true }) points: PointEntity[];
    @Input({ required: true }) visualization: VisualizationEntity;
    @Input({ required: true }) options: VisualizationOptionsType;
    @Input({ required: true }) company: CompanyEntity;

    public indicatorProps: IndicatorComparationsResultType[] = [];
    public chartStructure: any = {};
    public isCustomStructure: boolean = true;

    constructor(
        private readonly getIndicatorComparationsController: GetIndicatorComparationsController
    ) {}

    ngOnInit(): void {
        this.setVisualization();
    }

    private setVisualization() {
        if (
            this.options.comparation.active ||
            this.options.budget ||
            this.options.projection ||
            this.options.goals
        ) {
            this.isCustomStructure = false;
            this.setVisualizationComparation();
        } else {
            this.setNormalVisualization();
        }
    }

    private setVisualizationComparation() {
        const chart: any = {
            chart: {
                type: "column",
                reflow: true
            },
            plotOptions: {
                series: {
                    borderWidth: 0,
                    marker: { enabled: true, radius: 4 },
                    animation: {
                        duration: 1000
                    },
                    opacity: 0.66,
                    states: {
                        hover: {
                            enabled: true,
                            opacity: 1
                        }
                    }
                }
            },
            series: [
                {
                    _id: "dfijklegHRucwaP6edHc",
                    unit: "day",
                    name: "PMP",
                    color: "cyan.dark",
                    type: "column"
                },
                {
                    _id: "H6ZxvW9j9Ydxyq7tz",
                    unit: "day",
                    name: "PME",
                    color: "cyan.medium",
                    type: "column"
                },
                {
                    _id: "eRm54xkHHPogxewgh",
                    unit: "day",
                    name: "PMR",
                    color: "cyan.light",
                    type: "column"
                },
                {
                    _id: "tgTdo5CkuaheS6eME",
                    unit: "day",
                    name: "Ciclo Financeiro",
                    color: "purple.medium",
                    type: "column"
                },
                {
                    _id: "cpqQ9HdHMdwYGyM34",
                    unit: "day",
                    name: "Ciclo Operacional",
                    color: "yellow.medium",
                    type: "column"
                }
            ],
            legend: {
                enabled: true
            },
            yAxis: {
                reversed: false,
                title: {
                    enabled: false
                },
                plotLines: [
                    {
                        width: 1,
                        value: 0,
                        zIndex: 6
                    }
                ],
                labels: {
                    enabled: true
                }
            }
        };

        if (this.options.goals && this.visualization?.props.goals?.length) {
            const goals: any[] = [];
            chart.series.forEach((serie: any) => {
                const goal = this.visualization.props?.goals.find(
                    (g) => g.s == serie._id
                );
                goals.push({ _id: goal.r, color: serie.color, type: "line" });
            });
            chart.goals = goals;
        }

        this.visualization.setChartConfiguration(chart);
    }

    private setNormalVisualization(): void {
        const _chekIsZeroNegative = (value: number) => {
            return value.toString()[0] == "-" && value.toString()[1] == "0"
                ? Math.abs(value)
                : value;
        };

        const financialCycle = this.getLast("Ciclo Financeiro"),
            operatingCycle = this.getLast("Ciclo Operacional"),
            apd = this.getLast("PMP"),
            ard = this.getLast("PMR"),
            asd = this.getLast("PME");
        const isNegativeCycle = _chekIsZeroNegative(financialCycle.v);

        const deadlinesAndCycles = [
            0,
            apd.v,
            asd.v,
            ard.v + Math.abs(asd.v),
            apd.v,
            operatingCycle.v
        ];

        const _nth = (array: any[], n: number = 0) => {
            if (!Array.isArray(array)) return undefined;
            const index = n < 0 ? array.length + n : n;
            return array[index];
        };

        const label = {
            PMP: apd.v,
            PME: asd.v,
            PMR: ard.v + Math.abs(asd.v),
            CF: isNegativeCycle
        };

        const seriesLabels: { [key: string]: SerieLabelType } = {
            PMP: {
                abrev: "PMP",
                title: "Prazo Médio de Pagamento (PMP)",
                labelValue: apd.v.toFixed(0)
            },
            PME: {
                abrev: "PME",
                title: "Prazo Médio de Estocagem (PME)",
                labelValue: asd.v.toFixed(0)
            },
            PMR: {
                abrev: "PMR",
                title: "Prazo Médio de Recebimento (PMR)",
                labelValue: ard.v.toFixed(0)
            },
            CICLOFINANCEIRO: {
                abrev: "CF",
                title: "Ciclo Financeiro",
                labelValue: isNegativeCycle.toFixed(0)
            },
            CICLOOPERACIONAL: {
                abrev: "CO",
                title: "Ciclo Operacional",
                labelValue: operatingCycle.v.toFixed(0)
            }
        };

        const indicators = this.getIndicatorComparationsController.execute({
            company: this.company,
            series: this.series,
            options: this.options
        });

        const data = [
            {
                low: 0,
                high: _nth(deadlinesAndCycles, 1),
                label: apd.v,
                name: apd.name,
                color: ColorsService.getColor("cyan.dark")
            },
            {
                low: 0,
                high: _nth(deadlinesAndCycles, 2),
                label: asd.v,
                name: asd.name,
                color: ColorsService.getColor("cyan.medium")
            },
            {
                low: asd.v,
                high: _nth(deadlinesAndCycles, 3),
                label: ard.v,
                name: ard.name,
                color: ColorsService.getColor("cyan.light")
            },
            {
                low: isNegativeCycle < 0 ? _nth(deadlinesAndCycles, 3) : apd.v,
                high: isNegativeCycle < 0 ? apd.v : _nth(deadlinesAndCycles, 3),
                label: isNegativeCycle,
                name: financialCycle.name,
                color: ColorsService.getColor("purple.medium")
            },
            {
                low: 0,
                high: _nth(deadlinesAndCycles, 5),
                label: operatingCycle.v,
                name: operatingCycle.name,
                color: ColorsService.getColor("yellow.medium")
            }
        ];

        const chart = {
            chart: {
                // width: _.get(element[0], "clientWidth"),
                paddingTop: 0,
                marginTop: 10,
                marginRight: 30,
                marginBottom: 0,
                marginLeft: 24,
                type: "columnrange",
                inverted: true
            },
            title: {
                text: ""
            },
            credits: {
                enabled: false
            },
            exporting: {
                enabled: false
            },
            plotOptions: {
                stacking: "normal",
                columnrange: {
                    dataLabels: {
                        enabled: true
                    }
                },
                series: { pointWidth: 50 }
            },

            legend: {
                enabled: false
            },

            series: [
                {
                    data,
                    dataLabels: {
                        enabled: false
                    }
                }
            ],
            tooltip: {
                formatter: function (
                    this: Highcharts.TooltipFormatterContextObject
                ) {
                    const point = this.point as any;
                    const labelDay = point?.label > 1 ? "dias" : "dia";

                    return `${this.key}: ${point.label.toFixed(0)} ${labelDay}`;
                }
            },
            yAxis: {
                title: "",

                labels: {
                    formatter: function (
                        this: Highcharts.AxisLabelsFormatterContextObject
                    ) {
                        const name = {
                            buy: 0,
                            payment: label.PMP,
                            sale: label.PME,
                            receipt: label.PMR
                        };
                        if (name.buy == this.value) return "Compra";
                        if (name.payment == this.value) return "Pagamento";
                        if (name.sale == this.value) return "Venda";
                        if (name.receipt == this.value) return "Recebimento";
                        return "";
                    },
                    rotation: 270,
                    align: "right",
                    x: 6,
                    y: 1
                },
                tickPositioner: function () {
                    if (label.CF < 0) deadlinesAndCycles.sort((a, b) => a - b);

                    return deadlinesAndCycles;
                },
                opposite: true,
                gridLineDashStyle: "Dash"
            },
            xAxis: {
                labels: {
                    enabled: false
                },
                title: {
                    text: ""
                },
                lineWidth: 0,
                tickLength: 0
            }
        };

        const getFormatedDays = (
            indicator: IndicatorComparationsResultType
        ) => {
            const days = Math.floor(indicator.current);
            return days > 1 ? `${days} dias` : `${days} dia`;
        };

        this.indicatorProps = indicators.map((indicator) => {
            const serieLabel =
                seriesLabels[
                    StringUtils.normalizeND(indicator.serie.props.name)
                ];
            indicator.serie.setSerieLabel(serieLabel);

            const serieChart = data.find(
                (s) => s.name === indicator?.serie?.props?.name
            );
            indicator.hexPolarityColor = serieChart?.color;
            indicator.days = getFormatedDays(indicator);
            return indicator;
        });

        this.chartStructure = chart;
    }

    private getLast(name: string): LastPointType {
        const serie = this.series.find((s) => s.props.name === name);
        if (!serie) return null;
        const point = this.points
            .slice()
            .reverse()
            .find((s) => s.props.s === serie.props._id);

        return {
            v: point.props.m.v,
            name: serie.props.name
        };
    }
}

type LastPointType = {
    v: number;
    name: string;
};

export interface ISpecialChartColumnRangeComponent {
    series: SerieEntity[];
    points: PointEntity[];
    visualization: VisualizationEntity;
    options: VisualizationOptionsType;
    company?: CompanyEntity;
}
